<template>
  <div>
    <b-sidebar
      id="sidebar-withdrawal-request"
      sidebar-class="sidebar-lg"
      :visible="withdrawalRequestSidebar.visible"
      bg-variant="white"
      shadow
      backdrop
      no-header
      right
      @shown="onShow"
      @change="(val) => mutateWithdrawalRequestSidebar({ visible: val })"
      @hidden="clear"
    >
      <template #default="{ hide }">
        <!-- Header -->
        <div
          class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
        >
          <h4>Solicitar saque</h4>
          <div>
            <feather-icon
              class="ml-1 cursor-pointer"
              icon="XIcon"
              size="16"
              @click="hide"
            />
          </div>
        </div>
        <!-- Form -->
        <b-form
          class="p-2"
          @submit.prevent="onSubmit"
          @reset.prevent="resetForm"
        >
          <b-row>
            <b-col cols="12">
              <!--
                Este wrapper era `font-italic text-danger` e envolvia TUDO: prazos de depósito,
                valor da nota e a memória de cálculo inteira saíam em vermelho itálico, como se
                fossem erro. A memória precisava de `style` inline só para escapar do pai.

                Nenhum dos avisos aqui é urgente — são todos descritivos. O que de fato pede
                atenção ganha destaque próprio, no lugar onde acontece.
              -->
              <div class="text-sm-left">
                <b-row>
                  <b-col cols="12">
                    <span>
                      A solicitação será criada com todo o saldo disponível no
                      momento.
                    </span>
                  </b-col>
                  <br />
                  <template v-if="isEmployee">
                    <b-col v-if="employeePaymentsInfo" cols="12" class="mt-1">
                      <span>
                        Após solicitado, seu pagamento será depositado dia
                        {{ employeePaymentsInfo.payment_deadline }}. Se não for
                        um dia útil, será depositado no próximo dia útil.
                      </span>
                    </b-col>
                    <b-col v-if="employeePaymentsInfo" cols="12" class="mt-1">
                      <span>
                        Caso você solicite após o dia
                        {{ employeePaymentsInfo.payment_deadline }}, ele será
                        depositado após
                        {{ employeePaymentsInfo.deadline_new_payments }} dias
                        úteis.
                      </span>
                    </b-col>
                  </template>
                  <template v-else>
                    <b-col cols="12" class="mt-1">
                      <span v-if="presentBillingDate" class="mt-3">
                        Comissões serão lançadas até o dia
                        {{ presentBillingDate.nextDate | dateToStr }}.
                      </span>
                    </b-col>
                    <b-col v-if="paymentDeadline" cols="12" class="mt-1">
                      <span>
                        Após solicitado, seu pró-labore será depositado dia
                        {{ paymentDeadline.consultant_payment_deadline }}. Se
                        não for um dia útil, será depositado no próximo dia
                        útil.
                      </span>
                    </b-col>
                    <b-col v-if="paymentDeadline" cols="12" class="mt-1">
                      <span>
                        Caso você solicite após o dia
                        {{ paymentDeadline.consultant_payment_deadline }}, o
                        pró-labore será depositado após
                        {{ paymentDeadline.days }} dias úteis.
                      </span>
                    </b-col>
                    <!--
                      ⚠️ O guard é o MESMO da memória de cálculo abaixo, e a fonte do número é a
                      mesma. Antes eram duas chaves para o mesmo valor: o badge lia
                      `futuro_hub_invoice_amount` (a chave legada de topo) com `v-if` por
                      truthiness, e a memória lia `invoice_composition.hub_invoice_amount`.

                      Com a nota em zero o badge SUMIA e a memória ficava — enquanto o anexo do
                      PDF podia continuar obrigatório pelo outro ramo da regra
                      (`has_solutions_payments`). O PN via um campo de nota fiscal obrigatório,
                      sem nenhum valor ao lado, e sem nada explicando por quê.

                      Agora os dois aparecem e somem juntos, e zero é dito em vez de escondido.
                    -->
                    <b-col v-if="invoiceComposition" cols="12">
                      <b-badge
                        :variant="hubInvoiceAmount > 0 ? 'light-warning' : 'light-secondary'"
                        class="p-1 mt-1 w-100 text-wrap"
                        style="font-weight: bold"
                        data-testid="withdrawal-request-invoice-badge"
                      >
                        <template v-if="hubInvoiceAmount > 0">
                          Valor para emissão da nota fiscal:
                          {{ hubInvoiceAmount | toCurrency }}
                        </template>
                        <template v-else>
                          Nada a emitir nesta competência.
                        </template>
                      </b-badge>
                    </b-col>
                    <!--
                      A memória de cálculo NÃO depende do valor da nota ser maior que zero: com a
                      nota em R$ 0,00 ela continua explicando de onde veio esse zero, que é
                      justamente quando o PN mais precisa dela.
                    -->
                    <b-col
                      v-if="invoiceComposition"
                      cols="12"
                      class="mt-1"
                      data-testid="withdrawal-request-invoice-composition"
                    >
                      <div class="border rounded p-1 text-body">
                        <div
                          class="d-flex justify-content-between align-items-center mb-50"
                        >
                          <strong>Como chegamos nesse valor</strong>
                          <b-badge
                            variant="light-secondary"
                            data-testid="withdrawal-request-hub-company"
                          >
                            {{ hubCompanyLabel }}
                          </b-badge>
                        </div>
                        <div class="d-flex justify-content-between">
                          <span>Soluções financeiras</span>
                          <span
                            data-testid="withdrawal-request-financial-solutions-amount"
                          >
                            {{
                              invoiceComposition.financial_solutions_amount
                                | toCurrency
                            }}
                          </span>
                        </div>
                        <div class="d-flex justify-content-between">
                          <span>
                            Ajustes Futuro Hub
                            <small
                              v-if="!hubAdjustmentsComposeInvoice"
                              class="text-muted"
                            >
                              (não compõem a nota)
                            </small>
                          </span>
                          <span
                            data-testid="withdrawal-request-hub-adjustments-amount"
                          >
                            {{
                              invoiceComposition.hub_adjustments_amount
                                | toCurrency
                            }}
                          </span>
                        </div>
                        <!--
                          ⚠️ Link SÓ para quem já poderia abrir a lista de ajustes.
                          Esta tela é do PN e a lista está atrás do menu financeiro: um PN que
                          clicasse tomaria 403 do middleware `fixed_financial_menu_role`, e o
                          interceptor de `helpers/api.js` DESLOGA em 401/403/419 — o link
                          derrubaria a sessão dele. A condição é a mesma do router
                          (`canAccessFixedFinancialRoute`) e do menu (`canViewFixedRoleItem`).

                          Aparece mesmo com o ajuste em zero: zero pode ser um crédito de 500 com
                          um débito de 500 se anulando, e é exatamente aí que ver as linhas importa.
                        -->
                        <div v-if="canOpenHubAdjustments" class="text-right">
                          <b-link
                            :to="hubAdjustmentsRoute"
                            target="_blank"
                            title="Abre a lista de ajustes filtrada por este PN e pela Futuro Hub. A lista não tem filtro de competência."
                            data-testid="withdrawal-request-hub-adjustments-link"
                          >
                            <small>
                              Ver lançamentos
                              <feather-icon icon="ExternalLinkIcon" size="12" />
                            </small>
                          </b-link>
                        </div>
                        <div
                          class="d-flex justify-content-between border-top mt-50 pt-50"
                        >
                          <strong>Valor da nota fiscal</strong>
                          <strong
                            data-testid="withdrawal-request-hub-invoice-amount"
                          >
                            {{ hubInvoiceAmount | toCurrency }}
                          </strong>
                        </div>
                        <!--
                          As parcelas acima saem com o sinal real, mas o total é travado em zero
                          no backend: nota fiscal não pode ser negativa. Sem esta frase, a conta
                          simplesmente não fecha na tela e o PN não tem como saber por quê.
                        -->
                        <div
                          v-if="hubInvoiceWasClamped"
                          class="text-muted mt-50"
                          data-testid="withdrawal-request-invoice-clamped"
                        >
                          <small>
                            A soma ficou em {{ rawHubInvoiceAmount | toCurrency }}. A nota fiscal
                            não pode ser emitida com valor negativo, então o valor a emitir é
                            R$ 0,00.
                          </small>
                        </div>
                        <div
                          class="d-flex justify-content-between align-items-center mt-1 mb-50"
                        >
                          <strong>Fora da nota fiscal</strong>
                          <b-badge
                            variant="light-info"
                            data-testid="withdrawal-request-platform-company"
                          >
                            {{ platformCompanyLabel }}
                          </b-badge>
                        </div>
                        <div class="d-flex justify-content-between">
                          <span>Entrevistas pagas</span>
                          <span
                            data-testid="withdrawal-request-interviews-amount"
                          >
                            {{
                              invoiceComposition.interviews_amount | toCurrency
                            }}
                          </span>
                        </div>
                        <div class="d-flex justify-content-between">
                          <span>Assinaturas</span>
                          <span
                            data-testid="withdrawal-request-subscriptions-amount"
                          >
                            {{
                              invoiceComposition.subscriptions_amount
                                | toCurrency
                            }}
                          </span>
                        </div>
                        <div class="d-flex justify-content-between">
                          <span>Ajustes Futuro Plataforma</span>
                          <span
                            data-testid="withdrawal-request-platform-adjustments-amount"
                          >
                            {{
                              invoiceComposition.platform_adjustments_amount
                                | toCurrency
                            }}
                          </span>
                        </div>
                        <div
                          class="d-flex justify-content-between border-top mt-50 pt-50"
                        >
                          <strong>Total Futuro Plataforma</strong>
                          <strong
                            data-testid="withdrawal-request-platform-total-amount"
                          >
                            {{
                              invoiceComposition.platform_total_amount
                                | toCurrency
                            }}
                          </strong>
                        </div>
                        <small class="text-muted d-block mt-50">
                          A Futuro Plataforma paga sem exigir nota fiscal. Você
                          emite nota apenas para a Futuro Hub.
                        </small>
                        <template v-if="hasOtherAdjustments">
                          <div class="d-flex justify-content-between mt-1">
                            <span>Rolagem e outros ajustes</span>
                            <span
                              data-testid="withdrawal-request-other-adjustments-amount"
                            >
                              {{
                                invoiceComposition.other_adjustments_amount
                                  | toCurrency
                              }}
                            </span>
                          </div>
                          <small class="text-muted d-block">
                            Compõem o saldo a sacar, mas não a nota fiscal.
                          </small>
                        </template>
                      </div>
                    </b-col>
                  </template>
                </b-row>
              </div>
            </b-col>
          </b-row>
          <b-row>
            <b-col cols="12" class="mt-2">
              <b-form-group label="Valor" label-for="withdrawal-amount">
                <b-form-input
                  id="withdrawal-amount"
                  v-money="moneyFormat"
                  disabled
                  :value="maxAmount"
                />
              </b-form-group>
            </b-col>
          </b-row>
          <b-row v-if="isDocumentRequired">
            <b-col cols="12" md="12" class="mt-1">
              <b-form-group
                label="Nota fiscal (apenas PDF)"
                label-for="withdrawal-document"
              >
                <!--
                  O anexo é exigido por dois ramos INDEPENDENTES no backend: existir comissão de
                  solução financeira na fatura, ou a nota ser maior que zero. Quando só o primeiro
                  vale, o campo aparece obrigatório ao lado de uma nota de R$ 0,00 — e sem esta
                  frase o PN abre chamado, com razão.
                -->
                <div
                  v-if="documentRequiredWithoutInvoiceValue"
                  class="text-muted mb-50"
                  data-testid="withdrawal-request-document-reason"
                >
                  <small>
                    A nota continua obrigatória porque esta competência tem comissões de soluções
                    financeiras.
                  </small>
                </div>
                <b-form-file
                  :disabled="saving"
                  id="withdrawal-document"
                  accept="application/pdf"
                  placeholder="Clique para anexar..."
                  v-model="document"
                  custom-file-label="Buscar"
                  :class="{ 'is-invalid': v$.document.$error }"
                />
                <div class="invalid-feedback">
                  <span v-if="v$.document.required.$invalid">
                    Você deve informar uma nota fiscal em formato PDF.
                  </span>
                  <span v-if="v$.document.maxSize.$invalid">
                    O arquivo deve ter tamanho de até 5MB.
                  </span>
                </div>
              </b-form-group>
            </b-col>
          </b-row>

          <!-- Form Actions -->
          <div class="d-flex mt-2">
            <b-button
              :disabled="saving"
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="primary"
              class="mr-2"
              type="submit"
            >
              {{ saving ? "Salvando..." : "Salvar" }}
            </b-button>
          </div>
        </b-form>
      </template>
    </b-sidebar>
  </div>
</template>

<script>
import {
  BSidebar,
  BForm,
  BFormGroup,
  BFormFile,
  BButton,
  BFormTextarea,
  BCol,
  BRow,
  BFormInput,
  BBadge,
  BLink,
} from "bootstrap-vue";
import { mapMutations, mapGetters, mapActions } from "vuex";
import vSelect from "vue-select";
import Ripple from "vue-ripple-directive";
import useVuelidate from "@vuelidate/core";
import { required, requiredIf } from "@vuelidate/validators";
import { useToast } from "vue-toastification/composition";
import { floatToMoney, moneyToFloat } from "@/helpers/converters";
import ToastificationContent from "@core/components/toastification/ToastificationContent";
import MoneyInput from "@/modules/shared/components/MoneyInput";
import { VMoney } from "v-money";
import { getBRLFormat } from "@/helpers/formatting";
import {
  HUB_ID,
  PLATFORM_ID,
  payingCompanyLabel,
} from "@/constants/paying_companies";
import {
  ADMIN_CREDENTIALS_STORAGE_KEY,
  canAccessFinancialMenu,
} from "@/constants/auth";
import { getCredentials } from "@/helpers/credentials";
import { FINANCIAL_ADJUSTMENTS, READ_ACTION } from "@/constants/resources";
import * as types from "../store/types";
import * as sharedTypes from "@/modules/shared/store/types";
import * as accountTypes from "@/modules/account/store/types";

export default {
  components: {
    // BSV
    BButton,
    BSidebar,
    BForm,
    BFormGroup,
    BFormTextarea,
    BFormFile,
    BCol,
    BRow,
    BFormInput,
    BBadge,
    BLink,
    vSelect,
    MoneyInput,
  },
  directives: {
    Ripple,
    money: VMoney,
  },
  data() {
    return {
      saving: false,
      document: undefined,
      isDocumentRequired: true,
    };
  },
  setup() {
    return { toast: useToast(), v$: useVuelidate() };
  },
  validations() {
    return {
      document: {
        required: requiredIf(this.isDocumentRequired),
        maxSize: function (file) {
          if (this.isDocumentRequired) {
            return file && file.size <= 5000000;
          }
          return true;
        },
      },
    };
  },
  computed: {
    ...mapGetters({
      withdrawalRequestSidebar: types.WITHDRAWAL_REQUEST_SIDEBAR,
      withdrawalSummary: sharedTypes.WITHDRAWAL_SUMMARY,
      presentBillingDate: sharedTypes.PRESENT_BILLING_DATE,
      paymentDeadline: sharedTypes.BILLING_PAYMENT_DEADLINE,
      employeePaymentsInfo: sharedTypes.EMPLOYEE_PAYMENTS_INFO,
      user: accountTypes.USER,
    }),
    moneyFormat: function () {
      return getBRLFormat();
    },
    maxAmount: function () {
      return floatToMoney(this.withdrawalSummary?.balance) || 0;
    },
    isEmployee: function () {
      return this.user?.is_employee || false;
    },
    invoiceComposition: function () {
      return this.withdrawalSummary?.invoice_composition;
    },
    // A ÚNICA fonte do valor da nota nesta tela. `futuro_hub_invoice_amount`, no topo da
    // resposta, é a mesma coisa — alias legado mantido para o backend poder subir antes do
    // frontend. Duas chaves para o mesmo número foi o que deixou o badge e a memória
    // discordarem: um sumia em zero e o outro não.
    hubInvoiceAmount: function () {
      return Number(this.invoiceComposition?.hub_invoice_amount) || 0;
    },
    // A soma CRUA, antes do `max(0.0, …)` que o backend aplica. Nota fiscal não pode ser
    // negativa, mas as parcelas são exibidas com o sinal real — então sem esta conta o PN lê
    // "−R$ 200,00 + R$ 0,00 = R$ 0,00" e não tem como saber que houve um travamento.
    rawHubInvoiceAmount: function () {
      const solucoes = Number(this.invoiceComposition?.financial_solutions_amount) || 0;
      const ajustes = this.hubAdjustmentsComposeInvoice
        ? Number(this.invoiceComposition?.hub_adjustments_amount) || 0
        : 0;
      return solucoes + ajustes;
    },
    hubInvoiceWasClamped: function () {
      return this.rawHubInvoiceAmount < 0;
    },
    // O anexo é exigido por DOIS ramos independentes no backend: existir comissão de solução
    // financeira na fatura, OU a nota ser maior que zero. Quando só o primeiro vale, o PN vê um
    // campo obrigatório ao lado de uma nota zerada — e sem esta frase não há como ele entender.
    documentRequiredWithoutInvoiceValue: function () {
      return this.isDocumentRequired && this.invoiceComposition && this.hubInvoiceAmount === 0;
    },
    // Kill switch do negócio (nf_includes_hub_adjustments). Desligado, o ajuste da Hub continua
    // no saldo mas some da nota — e o PN precisa ver isso escrito, senão a conta não fecha na mão.
    hubAdjustmentsComposeInvoice: function () {
      return this.invoiceComposition?.hub_adjustments_compose_invoice || false;
    },
    // Rolagem não tem CNPJ: não é da Hub nem da Plataforma. Só aparece quando existe, para não
    // criar uma linha zerada que o PN teria que decifrar.
    hasOtherAdjustments: function () {
      return Number(this.invoiceComposition?.other_adjustments_amount) !== 0;
    },
    hubCompanyLabel: function () {
      return payingCompanyLabel(HUB_ID);
    },
    platformCompanyLabel: function () {
      return payingCompanyLabel(PLATFORM_ID);
    },
    /**
     * Espelha `canViewFixedRoleItem` (@core/libs/acl/utils) e `canAccessFixedFinancialRoute`
     * (libs/acl/routeProtection), que por sua vez espelham o middleware
     * `fixed_financial_menu_role:financial_adjustment:manage,financial_adjustment:read`:
     * role fixa do menu financeiro OU ability CASL no resource.
     *
     * ⚠️ Basta testar `read`: no CASL a ação `manage` é curinga, então quem tem
     * `financial_adjustment:manage` também passa em `$can('read', ...)`.
     *
     * O consultor precisa existir junto — sem id não há como filtrar a lista por este PN, e um
     * link que abre a lista inteira do Financeiro não é o que a memória de cálculo promete.
     */
    canOpenHubAdjustments: function () {
      if (!this.user?.appify_consultant_id) return false;
      const credentials = getCredentials(ADMIN_CREDENTIALS_STORAGE_KEY);
      return (
        Boolean(canAccessFinancialMenu(credentials?.user?.user_role_id)) ||
        this.$can(READ_ACTION, FINANCIAL_ADJUSTMENTS)
      );
    },
    hubAdjustmentsRoute: function () {
      return {
        name: "financial-adjustments-list",
        // Os nomes são os de `searchRules()` no backend; `consultant` vira array lá na tela de
        // destino porque o scope do model usa `whereIn`.
        query: {
          consultant: String(this.user.appify_consultant_id),
          paying_company_id: String(HUB_ID),
        },
      };
    },
  },
  methods: {
    floatToMoney,
    ...mapMutations({
      mutateWithdrawalRequestSidebar: types.MUTATE_WITHDRAWAL_REQUEST_SIDEBAR,
    }),
    ...mapActions({
      getWithdrawalSummary: sharedTypes.GET_WITHDRAWAL_SUMMARY,
      saveWithdrawalRequest: types.SAVE_WITHDRAWAL_REQUEST,
      getPresentBillingDate: sharedTypes.GET_PRESENT_BILLING_DATE,
      getPaymentDeadline: sharedTypes.GET_BILLING_PAYMENT_DEADLINE,
      getEmployeePaymentsInfo: sharedTypes.GET_EMPLOYEE_PAYMENTS_INFO,
      getWithdrawalDocumentRequired: types.GET_WITHDRAWAL_DOCUMENT_REQUIRED,
    }),
    onShow() {
      this.getWithdrawalDocumentRequired()
        .then((resp) => {
          if (resp.data) {
            this.isDocumentRequired = resp.data.required;
          }
        })
        .catch((error) => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: "Oops!",
              text: "Ocorreu um erro ao recuperar informações para saque. Entre em contato com o setor de TI.",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        });
      this.getWithdrawalSummary().catch((error) => {
        this.toast({
          component: ToastificationContent,
          props: {
            title: "Oops!",
            text: "Ocorreu um erro ao recuperar o saldo para saque. Entre em contato com o setor de TI.",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      });
      this.getPresentBillingDate().catch((error) => {
        this.toast({
          component: ToastificationContent,
          props: {
            title: "Oops!",
            text: "Ocorreu um erro ao recuperar as informações de pagamento. Entre em contato com o setor de TI.",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      });
      this.getPaymentDeadline().catch((error) => {
        this.toast({
          component: ToastificationContent,
          props: {
            title: "Oops!",
            text: "Ocorreu um erro ao recuperar as informações de pagamento. Entre em contato com o setor de TI.",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      });
      this.getEmployeePaymentsInfo().catch((error) => {
        this.toast({
          component: ToastificationContent,
          props: {
            title: "Oops!",
            text: "Ocorreu um erro ao recuperar as informações de pagamento. Entre em contato com o setor de TI.",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      });
    },
    async onSubmit() {
      const isFormCorrect = await this.v$.$validate();
      if (!isFormCorrect) return;
      this.saving = true;
      const payload = {
        document: this.document,
      };
      const request = new FormData();
      for (let [key, value] of Object.entries(payload)) {
        if (value && value !== "") {
          request.append(key, value);
        }
      }
      this.saveWithdrawalRequest(request)
        .then((response) => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: "Sucesso",
              text: "A solicitação de saque foi salva com sucesso!",
              icon: "CoffeeIcon",
              variant: "success",
            },
          });
          this.withdrawalRequestSidebar.saveAction();
          this.mutateWithdrawalRequestSidebar({ visible: false });
          this.$emit("formSaved");
        })
        .catch(() => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: "Oops!",
              text: "Ocorreu um erro ao salvar a solicitação. Entre em contato com o setor de TI.",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        })
        .finally(() => {
          this.saving = false;
        });
    },
    clear() {
      this.document = undefined;
      this.isDocumentRequired = true;
      this.v$.$reset();
    },
  },
};
</script>
